/**
 * Cliente HTTP da ticketeira.
 *
 * Mesmo formato do TMobi: POST com corpo JSON para
 * `https://{dominio}/rest/4.5/?token={token}&v={versao}&action={acao}`.
 * Nada aqui exige mudança no servidor.
 */

import {ticketeira} from '../config';
import type {Evento, IngressoOnline, Validado} from '../dominio/tipos';
import {
  formatarDataHora,
  lerCancelados,
  lerEventos,
  lerIngressosOnline,
  lerLotes,
  lerSetores,
  lerValidados,
} from './protocolo';
import type {IngressoCancelado, Lote, Setor} from '../dominio/tipos';

declare const __VERSAO_API__: string;

/**
 * A versão que este app anuncia ao servidor em toda chamada (`?v=`).
 *
 * Sobe a cada publicação — é assim no TMobi original, que manda ali o próprio
 * `versionName`. O último pedaço é a contagem de commits, carimbada pelo Vite
 * no build (ver `versaoDaApi` em vite.config.mts): o mesmo commit sempre
 * devolve o mesmo número.
 *
 * Não é o número que o operador lê na tela. Aquele é a data da subida, e vive
 * em `src/web/versao.ts` — quem pergunta "qual versão está aí?" e quem lê o
 * log do servidor querem coisas diferentes.
 *
 * Fora do build (teste, ferramenta) vale `1.0.0`, que o servidor já conhece.
 */
export const VERSAO_APP =
  typeof __VERSAO_API__ === 'string' && __VERSAO_API__ ? __VERSAO_API__ : '1.0.0';

/**
 * Tempo máximo por chamada — portaria não pode ficar pendurada esperando rede.
 * Mesmo valor do TMobi original, cujo `PrepareHttp()` monta o cliente com 20s.
 */
const TIMEOUT_MS = 20000;

/**
 * A carga completa dos nominais é a exceção: o original reserva 140s só para
 * ela (`PrepareHttp(140)` em `DownloadLotesForcado`), porque é o evento
 * inteiro num pedido só. Vale porque hoje ela só roda quando o operador pede.
 */
const TIMEOUT_CARGA_COMPLETA_MS = 140000;

export class ErroApi extends Error {
  constructor(
    message: string,
    readonly status?: number,
  ) {
    super(message);
    this.name = 'ErroApi';
  }
}

let tokenAtual = '';

export function definirToken(token: string): void {
  tokenAtual = token;
}

async function chamar<T>(
  acao: string,
  parametros: Record<string, string | number> = {},
  corpo: unknown = {},
  tempoLimite: number = TIMEOUT_MS,
): Promise<T> {
  const query = Object.entries(parametros)
    .map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`)
    .join('&');

  const url =
    ticketeira().apiBase +
    `?token=${encodeURIComponent(tokenAtual)}&v=${VERSAO_APP}&action=${acao}` +
    (query ? '&' + query : '');

  const controle = new AbortController();
  const relogio = setTimeout(() => controle.abort(), tempoLimite);
  const inicio = Date.now();

  // Sem isto, falha de rede só aparece na tela e some — e o logcat fica mudo
  // justamente na hora em que se precisa dele. Token nunca entra no log.
  const registrar = (desfecho: string) =>
    console.log(`[api] ${acao} ${desfecho} em ${Date.now() - inicio}ms`);

  try {
    const resposta = await fetch(url, {
      method: 'POST',
      headers: {'Content-Type': 'application/json'},
      body: JSON.stringify(corpo ?? {}),
      signal: controle.signal,
    });

    if (!resposta.ok) {
      registrar(`HTTP ${resposta.status}`);
      throw new ErroApi(`Servidor respondeu ${resposta.status}`, resposta.status);
    }

    const texto = await resposta.text();

    // O legado responde 200 com content-type JSON mesmo quando o PHP morreu no
    // meio — o corpo vem com "Fatal error"/HTML. Sem esta checagem o app diria
    // "sem conexão" para um erro que é do servidor.
    if (/^\s*</.test(texto) || /Fatal error|Warning:|Notice:/.test(texto)) {
      const resumo = texto
        .replace(/<[^>]*>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .slice(0, 160);
      registrar(`erro interno do servidor (${texto.length}B)`);
      throw new ErroApi(`Erro interno do servidor em "${acao}": ${resumo}`);
    }

    try {
      const dados = JSON.parse(texto) as T;
      registrar(`ok ${texto.length}B`);
      return dados;
    } catch {
      registrar('resposta ilegível');
      throw new ErroApi(
        `Resposta de "${acao}" não é JSON: ` + texto.slice(0, 120),
      );
    }
  } catch (erro) {
    if (erro instanceof ErroApi) {
      throw erro;
    }
    if ((erro as Error).name === 'AbortError') {
      registrar(`estourou o tempo de ${tempoLimite}ms`);
      throw new ErroApi('Servidor não respondeu a tempo');
    }
    registrar(`falha de rede: ${(erro as Error).message}`);
    throw new ErroApi('Falha de rede: ' + (erro as Error).message);
  } finally {
    clearTimeout(relogio);
  }
}

// --- Autenticação ------------------------------------------------------------

export interface RespostaAuth {
  logged?: boolean;
  notFound?: boolean;
  nome?: string;
  remoteId?: number;
  token?: string;
  nivel?: number;
  type?: number;
  device_id?: number;
}

/**
 * Login do operador direto na ticketeira compilada — a empresa não é escolhida
 * na tela, vem do flavor.
 */
export async function autenticar(
  usuarioId: string,
  pin: string,
  deviceId: string,
): Promise<{sessao: RespostaAuth; machineId: number; token: string}> {
  definirToken('');

  const resposta = await chamar<RespostaAuth>('auth', {
    userId: usuarioId,
    password: pin,
    deviceId,
    version: VERSAO_APP,
  });

  if (resposta.logged === false || resposta.notFound === true) {
    throw new ErroApi('ID ou PIN inválido');
  }
  if (!resposta.token) {
    throw new ErroApi('Servidor não devolveu token de acesso');
  }

  definirToken(resposta.token);

  return {
    sessao: resposta,
    machineId: Number(resposta.device_id ?? 0),
    token: resposta.token,
  };
}

/** Nível mínimo para liberar operação restrita — mesma régua do TMobi original. */
export const NIVEL_SUPERVISOR = 3;

export interface Autorizacao {
  autorizado: boolean;
  nome?: string;
  nivel?: number;
  motivo?: string;
}

/**
 * Confere credencial de quem tem acesso para liberar operação restrita (hoje:
 * mexer nos setores depois que a portaria começou).
 *
 * Não troca a sessão: o operador do caixa continua sendo quem estava logado —
 * quem libera é o supervisor, e ele vai embora depois de liberar.
 */
export async function verificarAcesso(
  usuarioId: string,
  senha: string,
  deviceId: string,
): Promise<Autorizacao> {
  const tokenDoOperador = tokenAtual;

  try {
    definirToken('');
    const resposta = await chamar<RespostaAuth>('auth', {
      userId: usuarioId,
      password: senha,
      deviceId,
      version: VERSAO_APP,
    });

    if (resposta.logged === false || resposta.notFound === true || !resposta.token) {
      return {autorizado: false, motivo: 'ID ou senha inválido'};
    }

    const nivel = Number(resposta.nivel ?? 0);
    if (nivel <= NIVEL_SUPERVISOR) {
      return {
        autorizado: false,
        nome: resposta.nome,
        nivel,
        motivo: `${resposta.nome ?? 'Este usuário'} não tem acesso para alterar setores`,
      };
    }

    return {autorizado: true, nome: resposta.nome, nivel};
  } finally {
    // Mesmo com falha, a sessão do operador volta como estava.
    definirToken(tokenDoOperador);
  }
}

export interface UsuarioDoServidor {
  remoteId: number;
  nome: string;
  nivel: number;
  /** Senha como o servidor manda — some daqui para dentro, virando resumo. */
  senha: string;
}

/**
 * Lista de usuários com nível, para conferir liberação sem rede depois.
 *
 * Só devolve quem tem acesso de supervisor: guardar credencial de gente que
 * não vai liberar nada é exposição à toa.
 */
export async function baixarUsuariosAutorizados(): Promise<UsuarioDoServidor[]> {
  const resposta = await chamar<{
    usuarios?: Array<{id: number; nm: string; pwd: string; lvl: number}>;
  }>('baixarUsuarios', {}, []);

  return (resposta.usuarios ?? [])
    .filter(u => Number(u.lvl ?? 0) > NIVEL_SUPERVISOR)
    .map(u => ({
      remoteId: Number(u.id),
      nome: String(u.nm ?? ''),
      nivel: Number(u.lvl ?? 0),
      senha: String(u.pwd ?? ''),
    }));
}

// --- Eventos -----------------------------------------------------------------

export async function baixarEventos(): Promise<Evento[]> {
  const resposta = await chamar<{eventos?: Array<Record<string, unknown>>}>(
    'getAllData',
    {},
    {v: VERSAO_APP, localData: []},
  );
  return lerEventos(resposta.eventos ?? []);
}

// --- Sincronização do evento -------------------------------------------------

export interface PacoteSincronizacao {
  lotes: Lote[];
  setores: Array<Omit<Setor, 'habilitado' | 'id'>>;
  cancelados: IngressoCancelado[];
  ingressosOnline: IngressoOnline[];
  validadosNoServidor: string[];
  /**
   * Marca de tempo do próprio servidor para o próximo delta. Usar o relógio do
   * aparelho aqui perderia validação se ele estiver adiantado.
   */
  datahora?: string;
}

type RespostaCodes = {
  codes?: Array<Array<string | number>>;
  online?: Array<Array<string | number>>;
  cancelados?: Array<Array<string | number>>;
  validados?: Array<Array<string | number>>;
  setores?: Array<Array<string | number>>;
  datahora?: string;
};

/**
 * A chamada central: sobe as validações pendentes deste aparelho e desce, no
 * mesmo pedido, o delta desde `desde` — lotes, setores, cancelados, ingressos
 * nominais e as validações feitas nos outros aparelhos.
 */
export async function sincronizarCodigos(parametros: {
  eventoId: number;
  machineId: number;
  desde: string;
  pendentes: Validado[];
}): Promise<PacoteSincronizacao> {
  const {eventoId, machineId, desde, pendentes} = parametros;

  const corpo = {
    validados: pendentes.map(v => [
      v.codbar,
      formatarDataHora(new Date(v.dataHora)),
    ]),
  };

  const resposta = await chamar<RespostaCodes>(
    'downloadCodes',
    {eventoId, machineId, datahora: desde},
    corpo,
  );

  return {
    lotes: lerLotes(resposta.codes ?? [], eventoId),
    setores: lerSetores(resposta.setores ?? [], eventoId),
    cancelados: lerCancelados(resposta.cancelados ?? [], eventoId),
    ingressosOnline: lerIngressosOnline(resposta.online ?? [], eventoId),
    validadosNoServidor: lerValidados(resposta.validados ?? []),
    datahora: resposta.datahora,
  };
}

/**
 * Carga completa dos ingressos nominais do evento (nome, CPF, e-mail, status).
 *
 * Sem uso hoje, e de propósito: o servidor estoura a memória do PHP nesta ação
 * e leva dez segundos para dizer que falhou. Quem recarrega o evento inteiro é
 * o `downloadCodes` com o cursor no começo, que é o mesmo caminho da abertura
 * (ver `prepararEvento`). Fica aqui porque a ação existe no protocolo — mas
 * antes de voltar a chamá-la, conferir se o servidor aguenta.
 */
export async function baixarIngressosOnline(
  eventoId: number,
): Promise<IngressoOnline[]> {
  const resposta = await chamar<RespostaCodes>(
    'downloadOnline',
    {eventoId},
    {},
    TIMEOUT_CARGA_COMPLETA_MS,
  );
  return lerIngressosOnline(resposta.online ?? [], eventoId);
}
